import { ArrowBack as ArrowBackIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { IMG } from '../theme/assets';

/**
 * まだグループが無い人に、案件の中身がどうなっているかを見せるだけの画面。
 *
 * 中身はすべて作り話だが、題材は持たせていない。実在の出来事らしい主張に
 * 判定が付いた画面は、切り取られると本物の編集判断に見えてしまうため。
 * 伝えたいのは「何を入れる箱で、仲間とどう使うか」だけ。
 */
const ITEMS = [
  { kind: '動画', name: '見本_VTR.mp4', desc: 'VTR・8分', found: 18, done: 16 },
  { kind: '文書', name: '見本_台本.docx', desc: '放送回の台本', found: 33, done: 33 },
  { kind: '文書', name: '見本_参考資料.pdf', desc: '下調べのまとめ', found: 12, done: 12 },
  { kind: '動画', name: '見本_リハーサル.mp4', desc: 'リハーサル録画', found: 6, done: 6 },
];

const SampleWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const tokens = useThemeColors();
  const { themeMode } = useThemeMode();

  const card = {
    borderRadius: '13px',
    border: `1px solid ${tokens.line}`,
    background: tokens.paper,
    boxShadow:
      themeMode === 'dark' ? '0 6px 18px rgba(0,0,0,.18)' : '0 6px 18px rgba(30,30,70,.05)',
  } as const;

  return (
    <Box
      sx={{ height: 'calc(var(--app-vh) - 64px - 64px)', display: 'flex', flexDirection: 'column' }}
    >
      {/* どこにいるか */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, flexShrink: 0 }}>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 17 }} />}
          onClick={() => navigate('/share')}
          sx={{ textTransform: 'none', fontWeight: 800, fontSize: 12.5, color: tokens.muted }}
        >
          グループ共有
        </Button>
        <ChevronRightIcon sx={{ fontSize: 15, color: tokens.faint }} />
        <Typography sx={{ fontSize: 12.5, color: tokens.muted }}>見本グループ</Typography>
        <ChevronRightIcon sx={{ fontSize: 15, color: tokens.faint }} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: tokens.ink }}>
          見本の案件
        </Typography>
        <Box
          component="span"
          sx={{
            ml: 0.5,
            fontSize: 10,
            fontWeight: 800,
            px: 1,
            py: '2px',
            borderRadius: '999px',
            background: 'rgba(224,134,0,.16)',
            color: '#e08600',
          }}
        >
          見本
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 2, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 21, fontWeight: 900, color: tokens.ink }}>
          見本の案件
        </Typography>
        <Typography sx={{ fontSize: 12, color: tokens.faint }}>
          共有アイテム：4 ・ メンバー3人
        </Typography>
      </Box>

      {/* 資料とやりとり */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateRows: 'minmax(0, 1fr)',
          gap: 2,
          opacity: 0.86,
        }}
      >
        <Box
          sx={{
            minHeight: 0,
            overflow: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
            gridAutoRows: 'min-content',
            gap: 1.75,
            pr: 0.5,
          }}
        >
          {ITEMS.map((it) => (
            <Box key={it.name} sx={{ ...card, overflow: 'hidden' }}>
              <Box
                sx={{
                  height: 86,
                  display: 'flex',
                  alignItems: 'flex-end',
                  p: 1.25,
                  position: 'relative',
                  background:
                    it.kind === '文書'
                      ? 'linear-gradient(135deg, #334066, #232a44)'
                      : 'linear-gradient(135deg, #2b3350, #1b2133)',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 10,
                    fontSize: 10,
                    fontWeight: 800,
                    color: '#fff',
                    background: 'rgba(0,0,0,.42)',
                    px: 0.9,
                    py: '2px',
                    borderRadius: '5px',
                  }}
                >
                  {it.kind}
                </Box>
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 900,
                    color: '#fff',
                    textShadow: '0 1px 3px rgba(0,0,0,.6)',
                  }}
                >
                  {it.name}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                <Typography sx={{ fontSize: 11, color: tokens.faint, mb: 1 }}>{it.desc}</Typography>
                <Box sx={{ display: 'flex', gap: 2.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 900, color: tokens.ink }}>
                      {it.found}
                    </Typography>
                    <Typography sx={{ fontSize: 9.5, color: tokens.faint }}>
                      見つかったチェック箇所
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 900, color: tokens.teal }}>
                      {it.done}
                    </Typography>
                    <Typography sx={{ fontSize: 9.5, color: tokens.faint }}>
                      チェックが済んだ箇所
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* 見本の終わり */}
      <Box
        sx={{
          ...card,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          mt: 2,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={IMG.workspace.owl}
          alt=""
          sx={{ width: 38, height: 38, objectFit: 'contain', flexShrink: 0 }}
        />
        <Typography sx={{ fontSize: 12, color: tokens.muted, lineHeight: 1.8 }}>
          これは見本です。アイテムを開くところから先は、ご自身のグループを作ってからお試しください。
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={() => navigate('/settings?tab=groups')}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            fontSize: 12.5,
            borderRadius: '9px',
            px: 2,
            py: 1,
            whiteSpace: 'nowrap',
            color: '#fff',
            background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})`,
            '&:hover': { background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})` },
          }}
        >
          グループを作る
        </Button>
      </Box>
    </Box>
  );
};

export default SampleWorkspace;
